import React from 'react';
import './Welcome.scss';
import { Link } from 'react-router-dom';
import Footer from '../components/Footer';

const Welcome = () => {
  return (
    <div className="landing">
      <div className="landing__header">
        <h1 className="landing__title">Cue the Content. Right on Cue.</h1>
        <p className="landing__subtitle">
          Real-time content sharing for events, meetings, and concerts - with
          accessibility at the heart.
        </p>
        <div className="landing__cta-container">
          <Link to="/account/register" className="landing__cta-button">
            Get Started Free
          </Link>
          <a href="#features" className="landing__cta-button secondary">
            Explore Features
          </a>
        </div>
      </div>

      <section className="landing__subheadline">
        <p>
          Make your event inclusive and engaging. Whether it&apos;s a public
          meeting, a concert, or a sing-along, your audience gets content
          instantly - on any device.
        </p>
      </section>

      <section className="landing__features" id="features">
        <div className="feature">
          <h2>📣 Real-Time Sharing</h2>
          <p>
            Push lyrics, slides, or announcements directly to your
            audience&apos;s screens - no downloads needed.
          </p>
        </div>

        <div className="feature">
          <h2>♿ Accessible by Design</h2>
          <p>
            Designed with accessibility in mind - no clutter, screen reader
            friendly and responsive across all devices.
          </p>
        </div>

        <div className="feature">
          <h2>🌍 Works Anywhere</h2>
          <p>
            No app installs. Attendees just scan a QR code or follow a link. It
            works on phones, tablets, and desktops.
          </p>
        </div>

        <div className="feature">
          <h2>🎨 Custom Branding</h2>
          <p>
            Make the experience yours with colors, logos, and layout tailored to
            your event.
          </p>
        </div>

        <div className="feature">
          <h2>🎶 Perfect for All Events</h2>
          <p>
            Whether it&apos;s a town hall, concert, or community singalong,
            ViewEventNotes keeps everyone connected and in sync.
          </p>
        </div>

        <div className="feature">
          <h2>💰 Free</h2>
          <p>
            It&apos;s free to get up and running. No credit card or subscription
            required to start sharing content. See{' '}
            <a href="#pricing">pricing plans</a> for additional features.
          </p>
        </div>
      </section>

      <section className="landing__usecases">
        <h2>Use Cases</h2>
        <ul>
          <li>
            <strong>Concerts & Performances</strong> Share lyrics, translations,
            or background info with the crowd.
          </li>
          <li>
            <strong>Public & Community Meetings</strong> Keep attendees informed
            and engaged with live agendas and notes.
          </li>
          <li>
            <strong>Workshops & Seminars</strong> Provide live updates,
            references, and feedback opportunities.
          </li>
        </ul>
      </section>
      <section className="landing__pricing">
        <h2 id="pricing">Pricing Plans</h2>
        <table className="pricing-table">
          <thead>
            <tr>
              <th>Features</th>
              <th>Free</th>
              <th>Basic (£5/mo)</th>
              <th>Premium (£30/mo)</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Create sessions</td>
              <td>✔</td>
              <td>✔</td>
              <td>✔</td>
            </tr>
            <tr>
              <td>Preview sessions</td>
              <td>✔</td>
              <td>✔</td>
              <td>✔</td>
            </tr>
            <tr>
              <td>Present sessions</td>
              <td>✔</td>
              <td>✔</td>
              <td>✔</td>
            </tr>
            <tr>
              <td>Multi-factor authentication</td>
              <td>✔</td>
              <td>✔</td>
              <td>✔</td>
            </tr>
            <tr>
              <td>Custom colour scheme</td>
              <td>✔</td>
              <td>✔</td>
              <td>✔</td>
            </tr>
            <tr>
              <td>Font selection</td>
              <td>✔</td>
              <td>✔</td>
              <td>✔</td>
            </tr>
            <tr>
              <td>Multiple showings</td>
              <td></td>
              <td>✔</td>
              <td>✔</td>
            </tr>
            <tr>
              <td>Welcome page logo</td>
              <td></td>
              <td>✔</td>
              <td>✔</td>
            </tr>
            <tr>
              <td>Welcome page background</td>
              <td></td>
              <td>✔</td>
              <td>✔</td>
            </tr>
            <tr>
              <td>End feedback link</td>
              <td></td>
              <td>✔</td>
              <td>✔</td>
            </tr>
            <tr>
              <td>End subscribe link</td>
              <td></td>
              <td>✔</td>
              <td>✔</td>
            </tr>
            <tr>
              <td>End donation link</td>
              <td></td>
              <td>✔</td>
              <td>✔</td>
            </tr>
            <tr>
              <td>Welcome custom page</td>
              <td></td>
              <td></td>
              <td>✔</td>
            </tr>
            <tr>
              <td>End custom pages</td>
              <td></td>
              <td></td>
              <td>✔</td>
            </tr>
            <tr>
              <td>Max sessions</td>
              <td>3</td>
              <td>Unlimited</td>
              <td>Unlimited</td>
            </tr>
            <tr>
              <td>Max attendees per session</td>
              <td>15</td>
              <td>100</td>
              <td>Unlimited</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section className="landing__howitworks">
        <h2>How It Works</h2>
        <ol>
          <li>
            <strong>Create your event</strong> Set up your event in seconds -
            add your content and go live.
          </li>
          <li>
            <strong>Share the link or QR code</strong> Instantly give access to
            attendees. Works on all modern devices.
          </li>
          <li>
            <strong>Broadcast in real-time</strong> Update content instantly
            during your event - from lyrics to live notes.
          </li>
        </ol>
      </section>

      <section className="landing__callout" id="get-started">
        <h2>Ready to Share Smarter?</h2>
        <p>
          Join a growing community of organisers making events more inclusive
          and interactive.
        </p>
        <Link to="/account/register" className="landing__cta-button">
          Create Your Free Account
        </Link>
      </section>

      <div className="landing__footer">
        <Footer />
      </div>
    </div>
  );
};

export default Welcome;
